import { Component } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { Location } from '@angular/common';
import { FormGroup, FormBuilder } from '@angular/forms';
import { DataService } from '../services/data.service';
import { FormService } from '../services/form.service';
import { HelperService } from '../services/helper.service';

declare let $: any;
declare let bootbox: any;

/**
	This is designed for extends only,
	act as the master/basic component of detail page, ie. create, edit, view

	using data.service as default data handler
	data service should in the form of { [modelName: string]: any }

	e.g.
	if modelName is set to be "user" and action is "create"
	it will look for data.user.create()

	using form.service as default form validation handler
	use setForm() to set the form for form.service
*/

@Component({
	selector: 'nm-detail-page',
	template: `Usage: create a new component and extends this class`
})
export class NmDetailPageClass {

	public form: FormGroup;
	public allowedActions: string[] = ['/create', '/edit/', '/view/'];
	public modelName: string;
	public action: string;
	public id: string;
	public rawData: any;
	public queryParams: any;
	public DateOfBirth: string;
	// displayed when submit success
	// override this if special message is required
	public successPromptMessage: {[action: string]: string} = {
		create: 'Successfully saved.',
		edit: 'Successfully updated.'
	}

	public renew_year: number;

	public constructor(
		public router: Router,
		public activatedRoute: ActivatedRoute,
		public location: Location,
		public data: DataService,
		public formBuilder: FormBuilder,
		public _form: FormService,
		public helper: HelperService
	) {
		this._getRouteInfo();
	}

	// called after setForm() before patchValue()
	// designed for logic supposed to run in constructor
	// override this is special handle is required
	public onBeforeRendered(): void {};

	public ngOnInit() {
		if(!this.modelName) console.error('Missing modelName.');

		this._form.setForm(this.form);
		this.onBeforeRendered();

		if(this.action == 'edit' || this.action == 'view') {
			this.patchValue();
		}

		if(this.action == 'view') {
			this._disableAllInputs();
		}

		this.renew_year = this.data.shared.memberOptions['membership_year'];
		
	}

	// get route information
	private _getRouteInfo(): void {
		// check whether it matches the allowed actions
		for(let i of this.allowedActions) {
			if(this.activatedRoute.routeConfig.path.indexOf(i) > -1){
				this.action = i.replace(/\//g, '');
				break;
			}
		}

		// get param
		this.activatedRoute.params.subscribe(params => {
			this.id = params['id'];
		});

		// get query params
		this.activatedRoute.queryParams.subscribe(params => {
			this.queryParams = params;
		});
	}

	// before submit handle
	// return data to submit
	// override this if special handle is required
	public onBeforeSubmit(data: any): any { return data; }

	// after submit handle
	// default show bootbox and then return to previous page
	// note that default submission failure handle
	// should be the resposibility of api dependency of data.service 
	// override this if special handle is required
	public onAfterSubmit(success: boolean): void {
		if(success) {
			bootbox.alert({
				message: this.successPromptMessage[this.action],
				callback:() => {
					this.onAfterSubmitAlertCallback();
				}
			});
		}
	};

	// callback for alert prompt after submit
	// override this if special handle is required
	public onAfterSubmitAlertCallback(): void {
		this.location.back();
	}

	// validation handle
	// default using form service to show error prompt
	// override this if special handle is required
	public onValidationFail(): void {
		this._form.showErrors();
	};

	// default submit handle
	// override this if special handle is required
	public onSubmit(): void {
		let data = JSON.parse(JSON.stringify(this.form.value)); // copy

		if(this.onBeforeSubmit) {
			data = this.onBeforeSubmit(data);
		}
		console.log(data);

		// stop from page
		if(data === false)
			return;

		if(this.form.valid) {
			try {
				let promise;
				if(this.action == 'create') {
					promise = this.data[this.modelName].create(data);
				}
				else if (this.action == 'edit') {
					promise = this.data[this.modelName].edit(this.id, data);
				}
				else {
					throw new Error(`Unknown action name: ${this.action}`);
				}

				promise
				.then(res => {
					this.onAfterSubmit(true);
				})
				.catch(err => {
					this.onAfterSubmit(false);
				});
			}
			catch(e) {
				console.error(e);
			}
		}
		else {
			this.onValidationFail();
		}
	}

	// default cancel handle
	// override this if special handle is required
	public onCancel(): void {
		this.location.back();
	}

	// default before patch value handle
	// override this if special handle is required
	public onBeforePatchValue(data: any): any | Promise<any> { return data; }

	// default after pathc value handle
	// override this if special handle is required
	public onAfterPatchValue(): void {}

	// default patch value handle
	// using data.modelName.view for getting record data
	// override this if special handle is required
	public patchValue(): void {
		try {
			(<Promise<any>>this.data[this.modelName]['view'](this.id))
			.then(res => {
				let data = res.json();

				if(data) {
					this.rawData = JSON.parse(JSON.stringify(data)); // copy
					let _handle = (data) => {
						this.form.patchValue(data);

						if(this.onAfterPatchValue) {
							this.onAfterPatchValue();
						}
					}

					if(this.onBeforePatchValue) {
						let _data = this.onBeforePatchValue(data);

						if(_data instanceof Promise) {
							_data
							.then(_handle)
							.catch(e => console.error(e));
						}
						else {
							_handle(_data);
						}
					}
					else {
						_handle(data);
					}
				}
				else {
					throw new Error('Record data not found');
				}
			}).catch(err => {
				console.error(err);
			});
		}
		catch(e) {
			console.error(`Method not defined, please ensure you have ${this.modelName}.view in data.service`);
			console.error(e);
		}
	}

	private _disableAllInputs(): void {
		// TODO: dummy way to disable all inputs
		setTimeout(() => {
			let selectors = `
				input,
				select,
				textarea,
				my-date-picker button,
				nm-reactive-select button,
				.checkbox label,
				.radio label
			`;
			$(selectors).not('.renewyear').prop('disabled', true);
			$(selectors).not('.renewyear').css({ 'cursor': 'not-allowed', 'pointer-events': 'none' });
			$('select').trigger('chosen:updated');
		}, 1000);
	}

}


// WEBPACK FOOTER //
// ./src/shared/nm-detail-page-class.ts